/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

import React from "react";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Table } from "antd";
import "../../Styles/styles-ResultsTable.css";
import "../../Styles/styles-Example.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useSelector } from "react-redux";


export function ComparisonChart({ dataIn, teamColors, home_page }) {

  const {
    comparisonCharts
  } = useSelector(({ compare }) => compare);

  let num_games_compared = dataIn.num_games;

  let arr = Array.from({ length: num_games_compared }, (_, i) => i);

  let colors = teamColors.all;


  const round = (num) => Math.round((num + Number.EPSILON) * 10) / 10;

  const rankColumn = {
    title: "Rank",
    dataIndex: "__rank",
    key: "__rank",
  };

  let tables = {};
  // One row mixes players per cell, so the injured flag is per cell. It lives in this
  // side map, keyed by metric, row position and column key, never on the row: the
  // row keys are the table columns.
  const injuredCells = {};
  let tableHeaderColumns = [rankColumn];
  for (let i = 0; i < dataIn.metrics.length; i++) {
    let metric = dataIn.metrics[i];
    let key = metric.key;
    let tableDataList = [];
    let tableDataObj = {};
    let tableHeaderColumn = {};
    let injuredRowCells = {};
    for (let j = 0; j < dataIn.data.length; j++) {
      tableDataObj = {};
      injuredRowCells = {};
      for (let k = 0; k < arr.length; k++) {
        let opponent_name = dataIn.data[j][k + "_opponent_name"];
        if (opponent_name === undefined) continue;
        let column_key = k + "_" + opponent_name?.replace(/\s+/g, '_')?.toLowerCase();
        if (i === 0 && j === 0 ) {
          tableHeaderColumn["title"] = opponent_name;
          tableHeaderColumn["dataIndex"] = column_key;
          tableHeaderColumn["key"] = column_key;
          tableHeaderColumn["onHeaderCell"] = () => {
            return {
              style: {
                backgroundColor: colors[k % colors.length],
                color: '#fff',
                textShadow: '0 0 1px #000, 0 0 1px #000, 0 0 1px #000'
              }
            };
          };
          tableHeaderColumns.push(tableHeaderColumn);
          tableHeaderColumn = {};
        }
        let dataKey = k + "_" + key + "__avg";
        if (dataIn.data[j][dataKey] === null || dataIn.data[j][dataKey] === undefined || dataIn.data[j][dataKey] === 0) continue;
        tableDataObj[column_key] = `${dataIn.data[j][dataKey]} - ${dataIn.data[j][dataKey + "_name"].split(" ")[1]}`;
        if (dataIn.data[j][dataKey + "_injured"] === true) injuredRowCells[column_key] = true;
      }
      if (Object.keys(tableDataObj).length > 0) {
        if (!injuredCells[key]) injuredCells[key] = {};
        injuredCells[key][tableDataList.length] = injuredRowCells;
        tableDataList.push(tableDataObj);
      }
    }
    if (tableDataList.length > 0) tables[key] = tableDataList;
  }

  Object.keys(tables).forEach((metricKey) => {
    const rows = tables[metricKey];
    if (rows.length === 0) return;

    const colKeys = Object.keys(rows[0]);
    const avgRow = colKeys.reduce((acc, colKey) => {
      const values = [];
      rows.forEach((row, idx) => {
        if (injuredCells[metricKey]?.[idx]?.[colKey] === true) return;
        const n = parseFloat(row[colKey]);
        if (!isNaN(n)) values.push(n);
      });
      acc[colKey] = values.length > 0
        ? <span style={{ fontWeight: 480 }}>{round(values.reduce((sum, v) => sum + v, 0) / values.length)}</span>
        : null;
      return acc;
    }, { __rank: "Avg" });

    const rankedRows = rows.map((row, i) => ({ ...row, __rank: i + 1 }));
    tables[metricKey] = [avgRow, ...rankedRows];
  });

  return (
    <>
      <div>
        {comparisonCharts && comparisonCharts.map(chart => 
          <div className="day-compare-chart-and-table">
            <h3> {chart.name} </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              marginBottom: '5rem',
              height: '260px'
            }}>
              <HighchartsReact highcharts={Highcharts}             
                options={{
                  ...chart.data,
                  chart: {
                    ...chart.data?.chart,
                    height: '260px'
                  }
                }}
              />
              <div className="team-table" style={{ overflowY: 'unset' }}>
                <div className="resultstable-table exportable tab-gpsdata gpsdv-table">
                  <Table
                    rowKey="Id"
                    columns={tableHeaderColumns}
                    size="small"
                    dataSource={tables[chart.key]}
                    rowClassName={"ant-table-row-no-shadow"}
                    pagination={false}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
